//? PLUGINS //
import {
    Box,
    BoxProps,
    Button,
    FormControl,
    Select,
    Grid,
    GridProps,
    useTheme,
    InputLabel,
    MenuItem,
    TextField as MuiTextField,
    SelectChangeEvent
} from "@mui/material";
import dayjs from "dayjs";
import { useField, useFormikContext } from "formik";
import React, { ChangeEvent, ChangeEventHandler, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as locationService from "services/location-service";
import * as tenantService from "services/tenant-service";
import * as applicationService from "services/application-service";
import { guestTenant } from "services/tenant-service";
import i18next from "i18next";

//? COMPONENTS //
import DatePicker from "components/formsUI/DatePicker";
import MultiSelect from "components/formsUI/MultiSelect";
import TextField from "components/formsUI/TextField";

//? STYLES //
import { ButtonProps } from "@mui/material/Button";
import * as Styles from "pages/requests/stepsIndividual/step2/step2.styles";
import { AxiosError } from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useTenantContext } from "contexts/tenantContext";
import { useSettingsContext } from "contexts/settingsContext";

//! ------------------------------------------------------------------------------------ //
interface Props {
    title: string;
    onBackClick: () => void;
    titlePath?: string;
}

const Content = ({ title, onBackClick, titlePath }: Props) => {
    const locale = i18next.language === undefined ? "nl" : i18next.language;
    const { setFieldValue, initialValues } = useFormikContext();
    const [field] = useField("dateFrom");
    const [locationsField] = useField('locations')
    const [zoneField] = useField('zone')
    const { t } = useTranslation();
    const theme = useTheme();
    const { applicationId, completionHash } = useParams()

    const [searchParams] = useSearchParams();
    const [locations, setLocations] = React.useState<string[]>([]);
    const [disabled, setDisabled] = React.useState<boolean>(false);
    const [zones, setZones] = React.useState<guestTenant[]>([]);
    const [selectedZone, _setSelectedZone] = React.useState<guestTenant | undefined>(undefined);
    const [zoneSelectOpen, setZoneSelectOpen] = React.useState<boolean>(false);
    const [disableZoneSelect, setDisableZoneSelect] = React.useState<boolean>(false);
    const { setCurrentTenant, currentTenant } = useTenantContext();
    const { getSettings } = useSettingsContext();

    const setSelectedZone = (zone: guestTenant | undefined) => {
        setFieldValue('zone', zone?.id)
        console.log(zone)
        _setSelectedZone(zone);
    }

    const getLocationNames = () => {
        if (selectedZone === undefined) return;
        if (selectedZone.id === undefined) return;
        locationService
            .getLocationNames(selectedZone.id)
            .then((res: string[]) => {
                setLocations(res);
            })
            .catch((error: AxiosError<Error>) => {
                // showError(`${t("certificates.error")}`);
                console.log("error");
            });
    };

    const getZones = () => {
        tenantService.getNames()
            .then((res: guestTenant[]) => {
                setZones(res);
            });
    }

    useEffect(() => {
        console.log(selectedZone)
        getLocationNames();
        if (completionHash !== undefined) {
            console.log('resetting locations', completionHash)
            console.log(locationsField.value)
            console.log(initialValues)
            setFieldValue('locations', [...(initialValues as any).locations])
        }
    }, [selectedZone])

    useEffect(() => {
        setDisableZoneSelect(false);
        if (!zones.length) {
            getZones();
        }
        if (titlePath && titlePath === 'requestAll.completeTitle') {
            setDisabled(true);
        }
        if (currentTenant !== null) {
            setSelectedZone(zones.find((zone) => zone.name.toLowerCase() === currentTenant.name.toLowerCase()));
            setDisableZoneSelect(true);
        }
    }, [zones, setDisabled]);

    // TODO : Add to global styles

    const gridContainerProps: GridProps = {
        container: true,
        spacing: 2,
        minHeight: { sm: 400, md: 450 },
    };

    const gridItemFullProps: GridProps = {
        item: true,
        xs: 12,
    };
    const gridItemHalfProps: GridProps = {
        item: true,
        xs: 6,
    };

    const titleProps: BoxProps = {
        component: "h1",
        sx: Styles.titleCSS(theme),
    };

    const handleZoneChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { target: { value } } = e;
        const zone = zones.find((zone) => zone.id === value);
        console.log(zone)
        setSelectedZone(zone);
    }

    const handleNextClick = () => {
        console.log('next');
        if (selectedZone === undefined) return;
        if (applicationId === undefined) return;
        // Only move application if it's not already in a zone
        if (completionHash !== undefined) return;
        applicationService.moveApplication(applicationId, selectedZone.id)
            .then((res) => {
                console.info(res)
                // alert(`setting tenant ${selectedZone.name}`)
                setCurrentTenant(selectedZone, { update: false });
                getSettings();
            })
    }

    function buttonProps(buttonName: string): ButtonProps {
        if (buttonName === "previous") {
            return {
                onClick: onBackClick,
                variant: "contained",
                sx: Styles.buttonCSS(theme, buttonName),
            };
        } else {
            return {
                onClick: handleNextClick,
                type: "submit",
                variant: "contained",
                sx: Styles.buttonCSS(theme, buttonName),
            };
        }
    }

    return (
        <Box sx={Styles.containerCSS(theme)}>
            <Grid {...gridContainerProps}>
                {/* Title */}
                <Grid {...gridItemFullProps}>
                    <Box {...titleProps}>{title}</Box>
                </Grid>
                <Grid {...gridItemFullProps}>
                    <Box sx={{ textAlign: 'justify', mb: "10px" }}>{t("requestIndividual.step3.info")}</Box>
                </Grid>
                {/* Date From */}
                <Grid {...gridItemHalfProps}>
                    <DatePicker
                        name="dateFrom"
                        label={t("requestIndividual.step3.dateFrom")}
                        minDate={dayjs()}
                        maxDate={dayjs().add(3, "month")}
                        disabled={disabled}
                    />
                </Grid>
                {/* Date To */}
                <Grid {...gridItemHalfProps}>
                    <DatePicker
                        name="dateTo"
                        label={t("requestIndividual.step3.dateTo")}
                        minDate={dayjs(field.value)}
                        maxDate={dayjs(field.value).add(1, "year")}
                        disabled={disabled}
                    />
                </Grid>
                <Grid {...gridItemFullProps}>
                    {disableZoneSelect && selectedZone && (
                        <MuiTextField
                            label="Zone"
                            value={selectedZone.displayName}
                            onChange={handleZoneChange}
                            disabled
                            select
                            fullWidth
                            SelectProps={{
                                value: selectedZone.id,
                                onOpen: () => setZoneSelectOpen(true),
                                onClose: () => setZoneSelectOpen(false),
                            }}
                            InputLabelProps={{
                                shrink: (selectedZone !== undefined ? selectedZone.displayName !== '' : false) || zoneSelectOpen,
                            }}
                        >
                            {zones.map((zone) => (
                                <MenuItem key={zone.id} value={zone.id}>{locale === 'nl' ? zone.displayName : locale === 'fr' ? zone.displayNameFrench : zone.displayName ?? zone.displayNameFrench}</MenuItem>
                            ))}
                        </MuiTextField>
                    )}
                    {!disableZoneSelect && (
                        <MuiTextField
                            label="Zone"
                            value={selectedZone}
                            onChange={handleZoneChange}
                            // disabled={disableZoneSelect}
                            select
                            fullWidth
                            SelectProps={{
                                onOpen: () => setZoneSelectOpen(true),
                                onClose: () => setZoneSelectOpen(false),
                            }}
                            InputLabelProps={{
                                shrink: (selectedZone !== undefined ? selectedZone.displayName !== '' : false) || zoneSelectOpen,
                            }}
                        >
                            {zones.map((zone) => (
                                <MenuItem key={zone.id} value={zone.id}>{locale === 'nl' ? zone.displayName : locale === 'fr' ? zone.displayNameFrench : zone.displayName ?? zone.displayNameFrench}</MenuItem>
                            ))}
                        </MuiTextField>
                    )}
                </Grid>
                {/* <Grid {...gridItemFullProps} /> */}
                {/* Locations */}
                <Grid {...gridItemFullProps}>
                    <MultiSelect
                        disabled={disabled || (selectedZone !== undefined ? selectedZone.name === '' : false)}
                        name="locations"
                        label={t("requestIndividual.step3.locations")}
                        options={locations}
                    />
                </Grid>
                {/* <Grid {...gridItemFullProps} /> */}
                {/* Reason */}
                <Grid {...gridItemFullProps}>
                    <TextField
                        name="reason"
                        label={t("requestIndividual.step3.reason")}
                        placeholder={t("requestIndividual.step3.reasonPlaceholder")}
                    />
                </Grid>
                {/* <Grid {...gridItemFullProps} /> */}
                {/* Function */}
                <Grid {...gridItemFullProps}>
                    <TextField
                        name="function"
                        label={t("requestIndividual.step3.function")}
                        placeholder={t("requestIndividual.step3.functionPlaceholder")}
                    />
                </Grid>
            </Grid>
            {/* Buttons */}
            <Box sx={Styles.buttonContainerCSS(theme)}>
                <Button {...buttonProps("previous")}>{t("buttons.previous")}</Button>
                <Button {...buttonProps("next")}>{t("buttons.next")}</Button>
            </Box>
        </Box>
    );
};

export default Content;
